"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { CollapseSidebarIcon, ExpandSidebarIcon } from "@/components/icons";
import { navigationItems } from "./menuConfig";
import styles from "./Navigation.module.css";

type NavigationProps = {
  variant: "desktop" | "mobile";
  isCollapsed?: boolean;
  onNavigate?: () => void;
  onToggle?: () => void;
};

function isActiveRoute(pathname: string, href: string) {
  return href === "/" ? pathname === href : pathname === href || pathname.startsWith(`${href}/`);
}

export function Navigation({ variant, isCollapsed = false, onNavigate, onToggle }: NavigationProps) {
  const pathname = usePathname();
  const toggleLabel = isCollapsed ? "Expand sidebar" : "Collapse sidebar";
  const SidebarToggleIcon = isCollapsed ? ExpandSidebarIcon : CollapseSidebarIcon;

  return (
    <aside
      id={variant === "desktop" ? "desktop-navigation" : undefined}
      className={styles.sidebar}
      data-variant={variant}
      data-collapsed={variant === "desktop" && isCollapsed ? "true" : "false"}
      aria-label="Primary navigation"
    >
      <nav className={styles.content}>
        <ul className={styles.menu}>
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = isActiveRoute(pathname, item.href);

            return (
              <li className={styles.menuItem} key={item.href}>
                <Link
                  className={styles.menuButton}
                  data-active={isActive ? "true" : undefined}
                  href={item.href}
                  aria-current={isActive ? "page" : undefined}
                  title={variant === "desktop" && isCollapsed ? item.label : undefined}
                  onClick={onNavigate}
                  onPointerUp={(event) => {
                    if (variant === "desktop" && isCollapsed) {
                      event.currentTarget.blur();
                    }
                  }}
                >
                  <Icon />
                  <span className={styles.menuButtonLabel}>
                    <span className={styles.menuButtonTitle}>{item.label}</span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>

        {variant === "desktop" && onToggle ? (
          <button
            className={styles.sidebarToggle}
            type="button"
            aria-label={toggleLabel}
            aria-controls="desktop-navigation"
            aria-expanded={!isCollapsed}
            title={`${toggleLabel} (⌘B / Ctrl+B)`}
            onClick={onToggle}
          >
            <SidebarToggleIcon />
          </button>
        ) : null}
      </nav>
    </aside>
  );
}
